import {
  SupplierInvoiceCreationStageType,
  SupplierRelationshipListResponseType,
} from "@sellar-io/api-v2/types";
import { getPaymentDue } from "@sellar-io/common";
import dayjs from "dayjs";
import {
  RadioSelector,
  RadioSelectorItem,
  InputNote,
} from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { EditCreditTermsFormFragment } from "./EditCreditTermsFormFragment";

export const EditPaymentTermsRadioSelector = ({
  paymentTerms,
  setPaymentTerms,
  setPaymentDueCalculateFrom,
  paymentDueCalculateFrom,
  paymentDue,
  setPaymentDue,
  invoiceCreationStage,
}: {
  paymentTerms:
    | SupplierRelationshipListResponseType[number]["paymentTerms"]
    | null
    | undefined;
  setPaymentTerms: (
    paymentTerms: SupplierRelationshipListResponseType[number]["paymentTerms"],
  ) => void;
  setPaymentDueCalculateFrom: (
    paymentDueCalculatedFrom: SupplierRelationshipListResponseType[number]["paymentDueCalculateFrom"],
  ) => void;
  paymentDueCalculateFrom:
    | SupplierRelationshipListResponseType[number]["paymentDueCalculateFrom"]
    | undefined;
  setPaymentDue: (paymentDue: number) => void;
  paymentDue: number | null | undefined;
  invoiceCreationStage: SupplierInvoiceCreationStageType | undefined;
}) => {
  const { business } = useSellarCurrentBusiness();
  const exampleDueDate =
    paymentTerms === "credit" && paymentDueCalculateFrom
      ? dayjs(
          getPaymentDue({
            paymentDue: paymentDue ?? null,
            paymentDueCalculateFrom,
          }),
        ).format("DD/MM/YYYY")
      : undefined;
  return (
    <div className={"flex flex-col gap-2"}>
      <RadioSelector
        onChange={(e) =>
          setPaymentTerms(
            e.currentTarget
              .value as SupplierRelationshipListResponseType[number]["paymentTerms"],
          )
        }
        value={paymentTerms ?? ""}
        required={true}
        variant="grouped"
      >
        <RadioSelectorItem
          value={"proforma"}
          title={"Pro-forma account"}
          description={""}
        />
        <RadioSelectorItem
          value={"paymentOnDelivery"}
          title={"Payment on delivery"}
          description={""}
        />
        <RadioSelectorItem
          value={"credit"}
          title={"Credit account"}
          description={""}
        />
      </RadioSelector>
      {paymentTerms === "credit" && (
        <>
          <EditCreditTermsFormFragment
            required={paymentTerms === "credit"}
            paymentDue={paymentDue}
            paymentDueCalculateFrom={paymentDueCalculateFrom}
            setPaymentDue={setPaymentDue}
            setPaymentDueCalculateFrom={setPaymentDueCalculateFrom}
          />
        </>
      )}
      {paymentTerms === "proforma" && (
        <InputNote>
          Pro-forma customers can pay upfront for their order at checkout if
          they are connected with Stripe. If not, you can issue an invoice
          before finalising their order, setting the payment due date to the
          date you issue the invoice.
        </InputNote>
      )}
      {paymentTerms === "paymentOnDelivery" && (
        <InputNote>
          Payment on delivery customers can make their payment up to or on the
          date of delivery. Their invoice will be available from when you
          finalise their order.
        </InputNote>
      )}
      {paymentTerms === "credit" && paymentDueCalculateFrom && (
        <InputNote>
          <>
            Example:{" "}
            {(invoiceCreationStage ??
              business?.Supplier?.invoiceCreationStage) === "confirmed"
              ? "Finalising"
              : (invoiceCreationStage ??
                    business?.Supplier?.invoiceCreationStage) === "dispatched"
                ? "Dispatching"
                : "Delivering"}{" "}
            an order from this customer today would produce a payment due date
            of: {exampleDueDate}
          </>
        </InputNote>
      )}
    </div>
  );
};
